import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import { UiSuspensefulOutlet } from '@experiences/ui-common';
import type { PropsWithChildren } from 'react';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { routePaths } from '../../common/constants/routePaths';
import { useOrganizationName } from '../../common/hooks/useOrganizationName';
import BreadcrumbProvider from '../../common/providers/BreadcrumbProvider';
import CheckGuard from '../../container/helpers/CheckGuard';
import type { UiRouteObject } from '../../container/routeConfigs/UiRouteObject';

const UnifiedExportComponent = React.lazy(() => import('./UnifiedExportComponent'));

const UnifiedExportProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const { formatMessage: translate } = useIntl();
    const organizationName = useOrganizationName();
    const EnableUnifiedExport = useFeatureFlagValue(Features.EnableUnifiedExport.name);

    const breadCrumbLinks = useMemo(() => [
        {
            index: 0,
            link: routePaths.admin,
            name: organizationName,
        },
        {
            index: 1,
            link: routePaths.adminUnifiedExport,
            name: translate({ id: 'CLIENT_UNIFIED_EXPORT' }),
        },
    ], [ organizationName, translate ]);

    return (
        <BreadcrumbProvider breadcrumbs={breadCrumbLinks}>
            {CheckGuard(EnableUnifiedExport, <>
                {children}
            </>)}
        </BreadcrumbProvider>
    );
};

export const UnifiedExportRoutes: UiRouteObject = {
    path: routePaths.adminUnifiedExport,
    element: (
        <UnifiedExportProvider>
            <UiSuspensefulOutlet />
        </UnifiedExportProvider>
    ),
    children: [
        {
            index: true,
            element: (
                <>
                    <UnifiedExportComponent />
                    <UiSuspensefulOutlet />
                </>
            ),
        },
    ],
};
